import * as Phaser from "phaser";
import { BasicScene } from "$/game/prefabs/scenes/BasicScene.ts";
import { PetGameObject } from "$/game/prefabs/PetGameObject.ts";
import { LampInteractiveObject } from "$/game/prefabs/home/LampInteractiveObject.ts";
import { petActions } from "$/game/emitters.ts";

export class SleepPetSystem {
  private tweenOut?: Phaser.Tweens.Tween;
  private tweenIn?: Phaser.Tweens.Tween;
  private tweenShake?: Phaser.Tweens.Tween;
  private petOriginalX: number;

  constructor(
    private myScene: BasicScene,
    private pet: PetGameObject,
    private lamp: LampInteractiveObject,
  ) {
    this.petOriginalX = this.pet.x;

    // Проверяем начальное состояние сна из данных питомца
    const petData = this.myScene.registry.get("pet");
    if (petData?.is_sleep) {
      // Если питомец уже спит - устанавливаем начальное состояние
      this.lamp.setInitialSleepState(true);
      // Сразу перемещаем питомца за экран (без анимации)
      this.pet.setX(this.petOriginalX + this.myScene.scale.width);
      // Небольшая задержка чтобы сцена успела инициализироваться
      this.myScene.time.delayedCall(100, () => {
        petActions.sleepStart();
      });
    }

    // Подписываемся на изменения данных питомца для синхронизации состояния сна
    this.myScene.registry.events.on(
      "changedata-pet",
      this.handlePetDataChange,
      this,
    );

    this.lamp.setOnClickCallback(() => {
      // Получаем конфигурацию сна с безопасным доступом и значением по умолчанию
      const gameConfig = this.myScene.global.TUNER_TWA_CONFIG.game as any;
      const sleepConfig = gameConfig.systems?.sleep;
      const transitionDuration = sleepConfig?.transitionDuration ?? 1000;

      const petData = this.myScene.registry.get("pet");

      // Если питомец спит
      if (petData?.is_sleep) {
        // Лампа включена (светло) - питомец возвращается
        this.tweenOut?.stop();
        this.tweenIn = this.myScene.tweens.add({
          targets: this.pet,
          x: this.petOriginalX,
          duration: transitionDuration,
          ease: "Sine.easeOut",
        });

        petActions.sleepEnd();
        this.myScene.api.pet.sleepEnd().then((response) => {
          if (response.data != null) {
            this.myScene.registry.set("pet", response.data);
          }
        });
      } // Если питомец не спит
      else {
        // Проверяем уровень энергии
        if (petData?.energy === 100) {
          // Энергия максимальная - делаем небольшое движение вправо и обратно
          this.tweenIn?.stop();
          this.tweenShake?.stop();

          // Движение вправо
          this.tweenOut = this.myScene.tweens.add({
            targets: this.pet,
            x: this.petOriginalX + 20, // Небольшое смещение вправо
            duration: transitionDuration / 2,
            ease: "Sine.easeOut",
          });

          // После этого возвращаем на место
          this.tweenShake = this.myScene.tweens.add({
            targets: this.pet,
            x: this.petOriginalX,
            duration: transitionDuration / 2,
            ease: "Sine.easeIn",
            delay: transitionDuration / 2,
          });

          // Не отправляем запрос на бэкенд, просто показываем анимацию
          // Также не изменяем состояние лампы
          // Явно сохраняем текущее состояние лампы
          const currentLampState = this.lamp.isOn;
          // Небольшая задержка чтобы убедиться, что состояние лампы не изменится
          this.myScene.time.delayedCall(50, () => {
            if (this.lamp.isOn !== currentLampState) {
              this.lamp.syncState(currentLampState);
            }
          });
        } else {
          // Лампа выключена (темно) - питомец засыпает и уходит за экран
          this.tweenIn?.stop();
          this.tweenOut = this.myScene.tweens.add({
            targets: this.pet,
            // влево или вправо, случайно
            x: this.petOriginalX +
              (Phaser.Math.Between(0, 1) ? -1 : 1) * this.myScene.scale.width,
            duration: transitionDuration,
            ease: "Sine.easeInOut",
          });

          petActions.sleepStart();
          this.myScene.api.pet.sleepStart().then((response) => {
            if (response.data != null) {
              this.myScene.registry.set("pet", response.data);
            }
          });
        }
      }
    });
  }

  /**
   * Обрабатывает изменения данных питомца для синхронизации состояния сна
   */
  private handlePetDataChange = (): void => {
    try {
      // Skip if scene is not active or pet is destroyed
      if (
        !this.myScene.scene.isActive() || !this.pet.active || !this.pet.body
      ) {
        return;
      }

      const petData = this.myScene.registry.get("pet");
      if (petData) {
        // Синхронизируем состояние лампы с состоянием сна питомца
        // Если питомец спит (is_sleep = true), то лампа должна быть выключена (isOn = false)
        // Если питомец не спит (is_sleep = false), то лампа должна быть включена (isOn = true)
        const isPetSleeping = petData.is_sleep;
        const shouldLampBeOn = !isPetSleeping;

        // Если питомец не спит, но у него максимальная энергия, предотвращаем выключение света
        // Разрешаем включение света, но не выключение
        const shouldPreventLightsOff = !isPetSleeping &&
          petData.energy === 100 && this.lamp.isOn && !shouldLampBeOn;

        // Если нужно изменить состояние лампы и это не запрещенный случай
        if (this.lamp.isOn !== shouldLampBeOn && !shouldPreventLightsOff) {
          // Состояние лампы не соответствует состоянию питомца - обновляем лампу
          this.lamp.syncState(shouldLampBeOn);
        }

        // Если питомец должен быть проснувшимся, но находится за экраном, вернем его
        if (!isPetSleeping && this.pet.x !== this.petOriginalX) {
          // Получаем конфигурацию сна с безопасным доступом и значением по умолчанию
          const gameConfig = this.myScene.global.TUNER_TWA_CONFIG.game as any;
          const sleepConfig = gameConfig.systems?.sleep;
          const transitionDuration = sleepConfig?.transitionDuration ?? 1000;

          // Останавливаем все активные твины
          this.tweenOut?.stop();
          this.tweenIn?.stop();
          this.tweenShake?.stop();

          // Анимация возврата питомца
          this.tweenIn = this.myScene.tweens.add({
            targets: this.pet,
            x: this.petOriginalX,
            duration: transitionDuration,
            ease: "Sine.easeOut",
          });

          // Отправляем событие пробуждения
          petActions.sleepEnd();
        }

        // Убедимся, что лампа включена, когда питомец просыпается
        if (!isPetSleeping && !this.lamp.isOn) {
          this.lamp.syncState(true);
        }

        // Дополнительная проверка для гарантии правильного состояния лампы
        if (
          !isPetSleeping && this.lamp.isOn !== shouldLampBeOn &&
          !shouldPreventLightsOff
        ) {
          this.lamp.syncState(shouldLampBeOn);
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  public destroy(): void {
    this.tweenOut?.stop();
    this.tweenIn?.stop();
    this.tweenShake?.stop();
    // Отписываемся от событий
    this.myScene.registry.events.off(
      "changedata-pet",
      this.handlePetDataChange,
      this,
    );
  }
}
